import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const LOGO_URL =
  'https://public.readdy.ai/ai/img_res/2e0133b5-41d8-4138-ab4b-b661eb93861d.png';

const serviceLinks = [
  { label: 'Mobile Lawn Mower Repair', href: '/services/lawn-mower' },
  { label: 'Snow Blower Repair & Maintenance', href: '/services/snow-blower' },
  { label: 'Generator Repair & Service', href: '/services/generator' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setServicesOpen(false);
  }, [location.pathname]);

  const isLight = scrolled || mobileOpen;
  const navBg = isLight
    ? 'bg-white border-b border-gray-200'
    : 'bg-transparent';
  const textColor = isLight ? 'text-gray-900' : 'text-white';
  const logoFilter = isLight ? '' : 'brightness-0 invert';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" aria-label="Road Tech Services Home">
          <img
            src={LOGO_URL}
            alt="Road Tech Services"
            className={`h-11 w-auto transition-all duration-300 ${logoFilter}`}
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-7">
          <Link
            to="/"
            className={`text-sm font-medium tracking-wide transition-colors hover:text-orange-500 ${textColor}`}
          >
            Home
          </Link>
          <Link
            to="/about"
            className={`text-sm font-medium tracking-wide transition-colors hover:text-orange-500 ${textColor}`}
          >
            About
          </Link>

          {/* Services Dropdown */}
          <div
            className="relative"
            ref={dropdownRef}
            onMouseEnter={() => setServicesOpen(true)}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <Link
              to="/services"
              className={`text-sm font-medium tracking-wide transition-colors hover:text-orange-500 flex items-center gap-1 ${textColor}`}
            >
              Services
              <i className="ri-arrow-down-s-line leading-none" />
            </Link>

            <div
              className={`absolute top-full left-0 pt-3 w-72 transition-all duration-200 ${
                servicesOpen
                  ? 'opacity-100 pointer-events-auto translate-y-0'
                  : 'opacity-0 pointer-events-none -translate-y-1'
              }`}
            >
              <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="flex items-center gap-3 px-5 py-4 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors border-b border-gray-50 last:border-0"
                  >
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-tools-line text-orange-500 text-sm" />
                    </div>
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          <Link
            to="/contact"
            className={`text-sm font-medium tracking-wide transition-colors hover:text-orange-500 ${textColor}`}
          >
            Contact
          </Link>

          <Link
            to="/consultation"
            className="bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors whitespace-nowrap"
          >
            Book a Consultation
          </Link>
        </div>

        {/* Mobile Toggle */}
        <button
          className={`lg:hidden cursor-pointer p-2 ${isLight ? 'text-gray-900' : 'text-white'}`}
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          <i
            className={`text-2xl leading-none ${
              mobileOpen ? 'ri-close-line' : 'ri-menu-line'
            }`}
          />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden bg-white border-t border-gray-100 overflow-hidden transition-all duration-300 ${
          mobileOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-6 space-y-1">
          <Link
            to="/"
            className="flex items-center justify-between py-3 text-gray-800 font-medium border-b border-gray-100 text-sm"
          >
            Home
          </Link>
          <Link
            to="/about"
            className="flex items-center justify-between py-3 text-gray-800 font-medium border-b border-gray-100 text-sm"
          >
            About
          </Link>
          <div className="border-b border-gray-100">
            <button
              className="flex items-center justify-between w-full py-3 text-gray-800 font-medium cursor-pointer text-sm"
              onClick={() => setServicesOpen(!servicesOpen)}
            >
              Services
              <i
                className={`ri-arrow-down-s-line text-lg transition-transform ${
                  servicesOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            {servicesOpen && (
              <div className="pb-3 pl-4 space-y-2">
                {serviceLinks.map((link) => (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="block py-2 text-sm text-gray-600 hover:text-orange-600 transition-colors"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
          <Link
            to="/contact"
            className="flex items-center justify-between py-3 text-gray-800 font-medium border-b border-gray-100 text-sm"
          >
            Contact
          </Link>
          <div className="pt-4">
            <Link
              to="/consultation"
              className="block bg-orange-600 hover:bg-orange-700 text-white text-center font-semibold px-5 py-3.5 rounded-lg transition-colors text-sm"
            >
              Book a Consultation
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}
